/*
 * The operator console's nav sections and the permission each requires to appear (admin-ui.md
 * "Navigation" visibility matrix). Section visibility is permission-driven and read from the JWT
 * claims: `visibleSections` is the single source both the SideNav (what to render) and the router
 * guard (what to admit) consult, so a role can never see a link it cannot open, nor deep-link past a
 * hidden one. Contests is the landing page (`/`); the rest are placeholder routes until their own
 * slices land.
 */

export interface NavSection {
  /** Stable key for React lists and route matching. */
  readonly key: string;
  /** The nav label (admin-ui.md). */
  readonly label: string;
  /** The route path; Contests, the landing page, sits at `/`. */
  readonly path: string;
  /** The permission that gates the section (admin-ui.md matrix). */
  readonly permission: string;
}

/** The landing section (Contests, at `/`) — where a fully authenticated operator lands. */
export const LANDING_SECTION: NavSection = {
  key: 'contests',
  label: 'Contests',
  path: '/',
  permission: 'contests:view',
};

export const NAV_SECTIONS: readonly NavSection[] = [
  LANDING_SECTION,
  { key: 'catalogue', label: 'Catalogue', path: '/catalogue', permission: 'catalogue:view' },
  { key: 'players', label: 'Players', path: '/players', permission: 'users:view' },
  { key: 'settlement', label: 'Settlement', path: '/settlement', permission: 'contests:settle' },
  { key: 'awards', label: 'Awards', path: '/awards', permission: 'awards:view' },
  {
    key: 'configuration',
    label: 'Configuration',
    path: '/configuration',
    permission: 'config:view',
  },
  { key: 'admins', label: 'Admins', path: '/admins', permission: 'admin:manage' },
];

/** The sections a permission set may see, in nav order (admin-ui.md visibility matrix). */
export function visibleSections(permissions: readonly string[]): readonly NavSection[] {
  const granted = new Set(permissions);
  return NAV_SECTIONS.filter((section) => granted.has(section.permission));
}

/**
 * The section an operator lands on: Contests when granted (every seeded role has `contests:view`),
 * otherwise their first visible section, or null when the token grants no section at all. Used to
 * route a denied deep link somewhere reachable instead of bouncing to a landing the operator also
 * cannot see.
 */
export function landingSection(permissions: readonly string[]): NavSection | null {
  return visibleSections(permissions)[0] ?? null;
}
